import { useDndContext } from "@dnd-kit/core";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import React from "react";

import { FileSystemNode } from "@/shared/domain/files/filesystem";
import { FileSystemNodeType } from "@/shared/domain/files/filesystem/fileSystemNode";

import styles from "./FileDragOverlay.module.css";

interface FileDragOverlayProps {
  activeDraggable: FileSystemNode | null;
  itemCount: number;
}

export const FileDragOverlay: React.FC<FileDragOverlayProps> = ({
  activeDraggable,
  itemCount,
}) => {
  const dndContext = useDndContext();
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const [nudge, setNudge] = React.useState({ nudgeX: 0, nudgeY: 0 });

  React.useLayoutEffect(() => {
    const leftStartPosition = dndContext.activeNodeRect?.left ?? 0;
    const topStartPosition = dndContext.activeNodeRect?.top ?? 0;
    const elementWidth = overlayRef.current?.clientWidth ?? 0;
    const elementHeight = overlayRef.current?.clientHeight ?? 0;

    const activatorEvent = dndContext.activatorEvent;
    let eventStartX = 0;
    let eventStartY = 0;
    if (activatorEvent instanceof PointerEvent) {
      eventStartX = activatorEvent.clientX;
      eventStartY = activatorEvent.clientY;
    }

    setNudge({
      nudgeX: eventStartX - leftStartPosition - elementWidth / 2,
      nudgeY: eventStartY - topStartPosition - elementHeight / 2,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showBadge = itemCount > 1;

  return (
    <div
      ref={overlayRef}
      data-cy="dnd-overlay"
      className={styles.overlay}
      style={{ left: nudge.nudgeX, top: nudge.nudgeY }}
    >
      {showBadge && (
        <span className={styles.badge}>{itemCount > 9 ? "9+" : itemCount}</span>
      )}
      <span className={styles.icon}>
        {activeDraggable?.type === FileSystemNodeType.Directory ? (
          <FolderRoundedIcon fontSize="small" color="inherit" />
        ) : (
          <InsertDriveFileOutlinedIcon fontSize="small" color="inherit" />
        )}
      </span>
      <span className={styles.name}>{activeDraggable?.name}</span>
    </div>
  );
};
